import cn from 'classnames';
import { ICellProps } from '@creditflowresearch/uilib/Table/DataTable';
import TruncateText from '@creditflowresearch/uilib/TruncateText';
import Status from '@/creditflowresearch/ui/common/components/TableCells/DealStatus';
import UpsizedCell from '@/creditflowresearch/ui/common/components/TableCells/UpsizedCell';
import TickerCell from '@/creditflowresearch/ui/common/components/TableCells/TickerCell';
import TrancheSizeCell from '@/creditflowresearch/ui/common/components/TableCells/TrancheSizeCell';
import CorporateLeads from '@/creditflowresearch/ui/components/deals/DealListTable/cells/CorporateLeads';
import {
  formatDataCell,
  formatNumberWithDecimals,
  getTrancheFieldWithPercent,
} from '@/creditflowresearch/ui/components/deals/helpers';
import IndustryCell from '@/creditflowresearch/ui/common/components/TableCells/IndustryCell';
import IssuersCell from '@/creditflowresearch/ui/components/deals/DealListTable/cells/IssuersCell';
import DealLink from '@/creditflowresearch/ui/components/deals/DealListTable/cells/DealLink';
import DealDateCell from '@/creditflowresearch/ui/common/components/TableCells/DealDateCell';
import MaturityDateCell from '@/creditflowresearch/ui/common/components/TableCells/MaturityDateCell';
import { RATING_AGENCIES_MAP } from '@/creditflowresearch/ui/components/bonds/helpers';

import alignStyles from '@creditflowresearch/uilib/styles/helpers/align.module.scss';
import spaceStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';
import textStyles from '@creditflowresearch/uilib/styles/helpers/text.module.scss';

export const getEmMarketColumns = () => ([
  {
    name: 'industry',
    title: 'sec/ind',
    width: 73,
    minWidth: 73,
    className: alignStyles.alignCenter,
    cellCallback: ({ row: { industry, subindustry } }) => (
      <IndustryCell industry={ industry } subindustry={ subindustry } />
    ),
  },
  {
    name: 'status',
    title: 'Status',
    width: 66,
    minWidth: 66,
    className: alignStyles.alignCenter,
    cellCallback: ({ row }) => <Status status={ row.status } />,
  },
  {
    name: 'date',
    title: 'Date',
    width: 106,
    minWidth: 106,
    className: alignStyles.alignLeft,
    cellCallback: ({ row }) => <DealDateCell row={ row } />,
  },
  {
    name: 'ticker',
    title: 'Ticker(s)',
    className: alignStyles.alignCenter,
    width: 70,
    minWidth: 70,
    cellCallback: (cellProps: ICellProps) => <TickerCell cell={ cellProps } isDeal isBadge />,
  },
  {
    name: 'primaryIssuer',
    title: 'issuer(s)',
    maxWidth: 338,
    cellCallback: (cellProps) => <IssuersCell { ...cellProps } maxWidth={ 338 } />,
  },
  {
    name: 'country',
    title: 'country',
    width: 110,
    maxWidth: 110,
    minWidth: 110,
    cellCallback: ({ row }) => <TruncateText value={ formatDataCell(row.country?.name, '-') } />,
  },
  {
    name: 'moRatings',
    title: 'MO',
    className: alignStyles.alignCenter,
    tooltipContent: 'Moody’s Rating',
    width: 53,
    cellCallback: ({ row }) => formatDataCell(row.ratings.find((rating) => rating?.ratingAgencyId === RATING_AGENCIES_MAP.ratingMoodys)?.abbreviation, '-'),
  },
  {
    name: 'spRatings',
    title: 'SP',
    className: alignStyles.alignCenter,
    tooltipContent: 'S&P Rating',
    width: 53,
    cellCallback: ({ row }) => formatDataCell(row.ratings.find((rating) => rating?.ratingAgencyId === RATING_AGENCIES_MAP.ratingSAndP)?.abbreviation, '-'),
  },
  {
    name: 'fitchRatings',
    title: 'FI',
    className: alignStyles.alignCenter,
    tooltipContent: 'Fitch Rating',
    width: 53,
    cellCallback: ({ row }) => formatDataCell(row.ratings.find((rating) => rating?.ratingAgencyId === RATING_AGENCIES_MAP.ratingFitch)?.abbreviation, '-'),
  },
  {
    name: 'size',
    title: 'Size (M)',
    width: 79,
    className: alignStyles.alignRight,
    cellCallback: ({ row: { size, benchmarkSize, status, sizeDescription } }) => (
      <TrancheSizeCell
        size={ size.size }
        currency={ size }
        sizeDescription={ sizeDescription }
        status={ status }
        benchmarkSize={ benchmarkSize }
      />
    ),
  },
  {
    name: 'upsized',
    width: 12,
    minWidth: 12,
    className: cn(alignStyles.alignLeft, spaceStyles.pn),
    cellCallback: ({ row: { upsized } }) => <UpsizedCell upsized={ !!upsized } />,
  },
  {
    name: 'tenor',
    title: 'TNR',
    width: 61,
    className: alignStyles.alignRight,
    cellCallback: ({ row }: ICellProps) => formatDataCell(row?.tenor && formatNumberWithDecimals(row.tenor, 2)),
  },
  {
    name: 'maturity',
    title: 'MTY',
    minWidth: 73,
    className: alignStyles.alignRight,
    cellCallback: ({ row }) => <MaturityDateCell row={ row } />,
  },
  {
    name: 'coupon',
    title: 'Coupon',
    width: 82,
    className: cn(alignStyles.alignRight, textStyles.nowrap),
    cellCallback: ({ row: { coupon } }: ICellProps) => formatDataCell(getTrancheFieldWithPercent(coupon, 3)),
  },
  {
    name: 'price',
    title: 'price',
    width: 82,
    className: cn(alignStyles.alignRight, textStyles.nowrap),
    cellCallback: ({ row }: ICellProps) => row.price ? formatDataCell(formatNumberWithDecimals(row.price, 5)) : '-',
  },
  {
    name: 'yield',
    title: 'yield',
    width: 82,
    className: cn(alignStyles.alignRight, textStyles.nowrap),
    cellCallback: ({ row }: ICellProps) => formatDataCell(row.yield, '-'),
  },
  {
    name: 'spread',
    title: 'spread',
    width: 82,
    className: alignStyles.alignRight,
    cellCallback: ({ row }: ICellProps) => formatDataCell(row.spread, '-'),
  },
  {
    name: 'leads',
    title: 'Leads',
    width: 143,
    className: alignStyles.alignLeft,
    cellCallback: (cellProps: ICellProps) => <CorporateLeads { ...cellProps } />,
  },
  {
    name: 'link',
    className: alignStyles.alignCenter,
    width: 32,
    cellCallback: DealLink,
  },
]);
